import { useCallback, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import Translator from "../../../components/helpers/Translator";
import { axiosPath } from "../../../../routing/helpers/router";
import minions from "../../../../minions/minions";
import useIsMounted from "../../../components/hooks/useIsMounted";
import { handleMinDate } from './tools/comsaReportsTools';

const ModuleComsaWasteManagementAdd = (props) => {
  const {
    reportOptions,
    reportList,
    codes,
    managerOptions,
    onAdd,
    moduleData,
  } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const currentPage = useSelector((state) => state.currentPage);
  const [showAddModal, setShowAddModal] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const dynamicAreaButton = useRef(null);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`}  />,
      value: i + 1,
    }))
  );

  const handleOnChangeCode = useCallback(
    (opt, setFieldValue, daIndex, managerId) => {
      // setFieldValue(`dynamicArea[${daIndex}].wasteManagement`, null);
      // setFieldValue(`dynamicArea[${daIndex}].operation`, null);
      // setFieldValue(`dynamicArea[${daIndex}].valuablePercentage`, '');
      setFieldValue(`dynamicArea[${daIndex}].code`, opt);

      if (currentPage?.options?.managers && opt && managerId) {
        const manager = currentPage?.options?.managers?.find(
          (m) => m.id === managerId
        );
        const managerCode = manager?.codes?.find((mc) => mc.codeId === opt);
        if (managerCode) {
          setFieldValue(
            `dynamicArea[${daIndex}].wasteManagement`,
            managerCode?.wasteManagement
          );
          setFieldValue(
            `dynamicArea[${daIndex}].operation`,
            managerCode?.operation
          );
          setFieldValue(
            `dynamicArea[${daIndex}].valuablePercentage`,
            managerCode?.valuablePercentage
          );
          setFieldValue(
            `dynamicArea[${daIndex}].intermediateManager`,
            managerCode?.intermediateManager
          );
        }
      }
    },
    [currentPage]
  );

  const handleOnChangeManager = useCallback(
    (opt, setFieldValue, daIndex, codeId) => {
      setFieldValue(`dynamicArea[${daIndex}].wasteManagement`, null);
      setFieldValue(`dynamicArea[${daIndex}].operation`, null);
      setFieldValue(`dynamicArea[${daIndex}].valuablePercentage`, "");
      setFieldValue(`dynamicArea[${daIndex}].intermediateManager`, "");
      setFieldValue(`dynamicArea[${daIndex}].manager`, opt);

      if (currentPage?.options?.managers && opt && codeId) {
        const manager = currentPage?.options?.managers?.find(
          (m) => m.id === opt
        );
        const managerCode = manager?.codes?.find((mc) => mc.codeId === codeId);
        if (managerCode) {
          setFieldValue(
            `dynamicArea[${daIndex}].wasteManagement`,
            managerCode?.wasteManagement
          );
          setFieldValue(
            `dynamicArea[${daIndex}].operation`,
            managerCode?.operation
          );
          setFieldValue(
            `dynamicArea[${daIndex}].valuablePercentage`,
            managerCode?.valuablePercentage
          );
          setFieldValue(
            `dynamicArea[${daIndex}].intermediateManager`,
            managerCode?.intermediateManager
          );
        }
      }
    },
    [currentPage]
  );

  // < ADD REPORT
  const addReport = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaReports_addReport"),
          minions.forms.toFormData({
            ...values,
            type: "wasteManagement",
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) {
            setShowAddModal(false);
            onAdd();
          }
        }),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  const handleAddReport = () => {
    setReportFormInitialValues({
      id: null,
      month: null,
      year: null,
      dynamicArea: [
        {
          code: null,
          manager: null,
          quantity: "",
          measureUnit: null,
          wasteManagement: null,
          operation: null,
          valuablePercentage: "",
          intermediateManager: "",
        },
      ],
      files: null,
      deletedFiles: [],
    });
    setShowAddModal(true);
  };
  // < ADD REPORT

  const removeDynamicArea = useCallback((dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  }, []);

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        "deletedFiles",
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue("deletedFiles", [...deletedFiles, salt]);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        code: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        // manager: Yup.number()
        //   .nullable().required(<Translator file="user" name="user.required" />),
        quantity: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        measureUnit: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        wasteManagement: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        operation: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        valuablePercentage: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      })
    ),
  });
  // < FORM SCHEMA

  return (
    <>
      <span
        className="btn btn-success btn-sm mr-2"
        onClick={handleAddReport}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="new.waste.management" />,
        )}
      >
        <AppRemixIcon icon="add" />
      </span>
      <AppModal
        show={showAddModal}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="report.form" />,
        )}
        size="lg"
        onClose={() => setShowAddModal(false)}
      >
        <Formik
          validateOnBlur={false}
          enableReinitialize
          initialValues={reportFormInitialValues}
          validationSchema={validationSchema}
          onSubmit={addReport}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={
                        <Translator file="module-comsaReports" name="year" />
                      }
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.year"
                        />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      title={
                        <Translator file="module-saicaReports" name="month" />
                      }
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors?.month}
                    />
                  </div>
                </div>
                <div className="col-sm-12">
                  <FieldArray
                    name="dynamicArea"
                    render={(arrayHelpers) => (
                      <>
                        {values.dynamicArea.map((da, daIndex) => (
                          <div
                            key={daIndex}
                            className="row pt-2 pb-2"
                            style={{ borderBottom: '1px solid #f4f6f9' }}
                          >
                            <div className="col-lg-12">
                              <button
                                className="btn btn-danger btn-xs float-right"
                                onClick={() =>
                                  removeDynamicArea(
                                    values.dynamicArea,
                                    arrayHelpers,
                                    daIndex,
                                  )
                                }
                                disabled={values.dynamicArea.length === 1}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="delete"
                                  />,
                                )}
                              >
                                <AppRemixIcon icon="delete-bin-6" />
                              </button>
                            </div>
                            <div className="col-md-6">
                              <AppSingleSelect
                                icon={<AppRemixIcon icon="barcode" />}
                                options={codes}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="code"
                                  />,
                                )}
                                value={values?.dynamicArea[daIndex]?.code}
                                onChange={(opt) =>
                                  handleOnChangeCode(
                                    opt,
                                    setFieldValue,
                                    daIndex,
                                    values?.dynamicArea[daIndex]?.manager,
                                  )
                                }
                                isLoading={!codes}
                                hasSearchBox
                                hasSelectOptions
                                error={
                                  errors?.dynamicArea &&
                                  errors?.dynamicArea[daIndex]?.code
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <AppSingleSelect
                                icon={<AppRemixIcon icon="user-settings" />}
                                options={managerOptions}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="managers"
                                  />,
                                )}
                                value={values?.dynamicArea[daIndex]?.manager}
                                onChange={(opt) =>
                                  handleOnChangeManager(
                                    opt,
                                    setFieldValue,
                                    daIndex,
                                    values?.dynamicArea[daIndex]?.code,
                                  )
                                }
                                isLoading={!managerOptions}
                                hasSearchBox
                                hasSelectOptions
                                error={
                                  errors?.dynamicArea &&
                                  errors?.dynamicArea[daIndex]?.manager
                                }
                                isClearable
                              />
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                {values?.dynamicArea[daIndex]?.manager ? (
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="waste.management.single"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="delete-bin-6" />}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .wasteManagement
                                        ? reportOptions?.wasteManagement.find(
                                            (wm) =>
                                              wm.value ===
                                              values.dynamicArea[daIndex]
                                                .wasteManagement,
                                          )?.label
                                        : ''
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.wasteManagement
                                    }
                                    isReadOnly
                                  />
                                ) : (
                                  <AppSingleSelect
                                    options={reportOptions.wasteManagement}
                                    onChange={(opt) => {
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].wasteManagement`,
                                        opt,
                                      );
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].operation`,
                                        null,
                                      );
                                    }}
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="waste.management.single"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="delete-bin-6" />}
                                    placeholder={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="select.a.waste.management"
                                      />,
                                    )}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .wasteManagement
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.wasteManagement
                                    }
                                  />
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                {values?.dynamicArea[daIndex]?.manager ? (
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="operation"
                                      />
                                    }
                                    icon={
                                      <AppRemixIcon icon="arrow-left-right" />
                                    }
                                    value={
                                      values.dynamicArea[daIndex].operation
                                        ? [
                                            ...reportOptions.eliminationOperation,
                                            ...reportOptions.valuationOperation,
                                          ].find(
                                            (o) =>
                                              o.value ===
                                              values.dynamicArea[daIndex]
                                                .operation,
                                          )?.label
                                        : ''
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]?.operation
                                    }
                                    isReadOnly
                                  />
                                ) : (
                                  <AppSingleSelect
                                    options={
                                      !values.dynamicArea[daIndex]
                                        .wasteManagement
                                        ? []
                                        : values.dynamicArea[daIndex]
                                              .wasteManagement === 23
                                          ? reportOptions.eliminationOperation
                                          : reportOptions.valuationOperation
                                    }
                                    onChange={(opt) =>
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].operation`,
                                        opt,
                                      )
                                    }
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="operation"
                                      />
                                    }
                                    icon={
                                      <AppRemixIcon icon="arrow-left-right" />
                                    }
                                    placeholder={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="select.an.operation"
                                      />,
                                    )}
                                    value={
                                      values.dynamicArea[daIndex].operation
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]?.operation
                                    }
                                  />
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                {values?.dynamicArea[daIndex]?.manager ? (
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="valuable.material.percentage"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="percent" />}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .valuablePercentage
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.valuablePercentage
                                    }
                                    onChange={(opt) =>
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].valuablePercentage`,
                                        opt,
                                      )
                                    }
                                    isReadOnly
                                  />
                                ) : (
                                  <AppInputNumber
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="valuable.material.percentage"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="percent" />}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .valuablePercentage
                                    }
                                    onChange={(value) =>
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].valuablePercentage`,
                                        value,
                                      )
                                    }
                                    decimals={3}
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.valuablePercentage
                                    }
                                  />
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                {values?.dynamicArea[daIndex]?.manager ? (
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="intermediate.manager"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="user" />}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .intermediateManager
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.intermediateManager
                                    }
                                    onChange={(opt) =>
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].intermediateManager`,
                                        opt,
                                      )
                                    }
                                    isReadOnly
                                  />
                                ) : (
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="intermediate.manager"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="user" />}
                                    value={
                                      values.dynamicArea[daIndex]
                                        .intermediateManager
                                    }
                                    onChange={(value) =>
                                      setFieldValue(
                                        `dynamicArea[${daIndex}].intermediateManager`,
                                        value,
                                      )
                                    }
                                    error={
                                      errors?.dynamicArea &&
                                      errors.dynamicArea[daIndex]
                                        ?.intermediateManager
                                    }
                                  />
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppInputNumber
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="quantity"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="scales-3" />}
                                  value={values.dynamicArea[daIndex].quantity}
                                  onChange={(value) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].quantity`,
                                      value,
                                    )
                                  }
                                  decimals={3}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.quantity
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={reportOptions.measureUnit}
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].measureUnit`,
                                      opt,
                                    )
                                  }
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="measure.unit"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="ruler-2" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.a.measure.unit"
                                    />,
                                  )}
                                  value={
                                    values.dynamicArea[daIndex].measureUnit
                                  }
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.measureUnit
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        {!values.id && (
                          <div className="row mt-3">
                            <div className="col-sm-12">
                              <span
                                className="pointer"
                                onClick={() =>
                                  arrayHelpers.push({
                                    code: null,
                                    quantity: '',
                                    measureUnit: null,
                                    wasteManagement: null,
                                    operation: null,
                                    valuablePercentage: '',
                                  })
                                }
                                ref={dynamicAreaButton}
                              >
                                <AppRemixIcon icon="add-box" />{' '}
                                <Translator
                                  file="module-comsaReports"
                                  name="add.more.types"
                                />
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className="col-12 mt-3 mb-3">
                  <label>
                    <Translator
                      file="module-comsaReports"
                      name="attach.evidence"
                    />
                  </label>
                  <AppFileUploader
                    isMulti
                    placeholder={
                      !values.files || Object.keys(values?.files).length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />,
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="general"
                              name="select.number.of.elements"
                              variables={{
                                elements: Object.keys(values?.files).length,
                              }}
                            />,
                          )
                    }
                    onChange={(opt) => setFieldValue('files', opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
              </div>
              {values.id &&
                Object.keys(reportList.find((r) => r.id === values.id).files)
                  .length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <label>
                        <Translator file="general" name="general.delete" />
                      </label>
                      <div>
                        {Object.keys(values.files).map((salt, index) => (
                          <div key={index}>
                            <label className="mb-2 pointer font-weight-light">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={salt}
                                onChange={() =>
                                  handleDeletedFiles(
                                    setFieldValue,
                                    values.deletedFiles,
                                    salt,
                                  )
                                }
                                checked={
                                  values.deletedFiles &&
                                  values.deletedFiles[salt]
                                }
                              />
                              {values.files[salt].name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <Translator
                      file="module-comsaReports"
                      name={values.id ? 'edit' : 'add'}
                    />
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
};

export default ModuleComsaWasteManagementAdd;
